import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SeedMark } from "./SeedMark";

const HERO_IMG =
  "https://images.unsplash.com/photo-1556983852-43bf21186b2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBmYWNhZGUlMjBncmVlbnxlbnwwfHx8fDE3ODcwMTY3Njd8MA&ixlib=rb-4.1.0&q=85";

const lines = ["Facilitando", "conquistas."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 24);
    my.set((e.clientY / innerHeight - 0.5) * 24);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      <div
        className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #00D765 0%, transparent 70%)" }}
      />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-[1.15fr_1fr] lg:px-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.25em] text-seed-salad"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block h-px w-10 bg-seed-salad" />
            Imobiliária · Urbano & Rural
          </motion.p>

          <h1 className="font-heading text-5xl font-black leading-[1.02] tracking-tighter text-seed-deep sm:text-6xl" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 1 ? (
                    <>
                      conquistas<span className="text-seed-vivid">.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-md text-base leading-relaxed text-seed-deep/70 md:text-lg"
            data-testid="hero-subtext"
          >
            A Seed Imóveis é a ponte entre o seu sonho e o endereço certo — casas, apartamentos,
            fazendas e chácaras com atendimento humano e soluções sob medida.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#imoveis"
              onClick={(e) => {
                e.preventDefault();
                window.__lenis?.scrollTo("#imoveis", { offset: -80 });
              }}
              data-testid="hero-cta-imoveis"
              className="rounded-full bg-seed-vivid px-8 py-4 font-heading text-sm font-bold text-seed-deep transition-transform duration-300 hover:scale-105"
            >
              Ver imóveis
            </a>
            <a
              href="https://wa.me/5534999236969?text=Ol%C3%A1!%20Quero%20falar%20com%20a%20Seed%20Im%C3%B3veis."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="rounded-full border border-seed-deep/20 px-8 py-4 font-heading text-sm font-bold text-seed-deep transition-colors duration-300 hover:border-seed-salad hover:text-seed-salad"
            >
              Falar no WhatsApp
            </a>
          </motion.div>
        </div>

        <div className="relative hidden justify-end lg:flex" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[520px] w-[380px] overflow-hidden"
            style={{ clipPath: "polygon(0 0, 100% 6%, 100% 100%, 0 94%)" }}
          >
            <motion.img
              src={HERO_IMG}
              alt="Arquitetura moderna com verde"
              style={{ y: imgY, scale: 1.2 }}
              className="h-full w-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            style={{ x: sx, y: sy }}
            className="absolute -left-10 bottom-16 drop-shadow-2xl"
            data-testid="hero-floating-mark"
          >
            <SeedMark size={96} animated />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-seed-deep/50"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
